import { useMemo, useState } from 'react';

import {
  CartItem,
  createCampaignSettings,
  usePrefetchShowCart,
  useShowCampaignById_new,
  useShowCart,
} from '@givebacks/api/store';
import { CellContext, useCrowdFundingTranslations } from 'core';
import {
  FullOrderModalStack,
  FullOrderModalStackModals,
  useFullOrderModalStack,
} from 'core/Components';
import { Table } from 'core/Packages';
import { IconArrowsDoubleNeSwTabler, IconReceipt, IconUsers } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../../../Context';
import { EditPledgeModal } from '../../../../../components/organisms/modals/EditPledgeModal';
import { EditCreditDesignationModal } from './Modals/EditCreditDesignationModal';

const CampaignDonorNameHeader = () => {
  const { causeId } = useCrowdfundingProvider();
  const { t } = useCrowdFundingTranslations(causeId);
  return <>{t('Donor')} Name</>;
};

const CampaignActions = (props: CellContext<CartItem, unknown>) => {
  const [isEditPledgeOpened, setIsEditPledgeOpened] = useState(false);

  const ctx = Table.useContext();
  const { campaignId, causeId } = ctx.table.options.meta as { campaignId: string; causeId: string };

  const [modalState, setModalState] = useState<'credit' | undefined>(undefined);
  const { stack, open: openStack } = useFullOrderModalStack();

  const { data: cartData } = useShowCart({
    cartId: props.row.original.cart?.uuid,
    causeId: props.row.original.cart?.cause_id,
  });

  const { data: campaign } = useShowCampaignById_new({
    campaignId,
    causeId,
  });

  const campaignSettings = useMemo(() => createCampaignSettings(campaign), [campaign]);

  const isCreditDesignationVisible = useMemo(() => {
    if (cartData?.status === 'refunded') return false;

    if (campaign?.__type === 'campaign' || campaign?.__type === 'athon') {
      if (campaign?.__type === 'athon' && campaign?.status === 'finished') return false;
      return campaignSettings.isPeersEnabled;
    }

    return false;
  }, [cartData?.status, campaign?.__type, campaign?.status, campaignSettings.isPeersEnabled]);

  const isViewFullOrderVisible = useMemo(() => cartData?.status != 'pledged', [cartData?.status]);

  const prefetchCart = usePrefetchShowCart({
    cartId: cartData?.uuid,
    causeId: cartData?.cause_id,
  });

  const isTicket = campaignSettings.type === 'ticket';

  if (!cartData) return null;

  return (
    <>
      <Table.RowAction onMouseEnter={prefetchCart}>
        {isTicket ? (
          <Table.RowAction.Option
            label="View Full Order"
            icon={IconReceipt}
            onClick={() => {
              openStack(FullOrderModalStackModals.FullOrder);
            }}
            visible={isTicket && isViewFullOrderVisible}
          />
        ) : (
          <>
            <Table.RowAction.Option
              label="Edit Credit Designation"
              icon={IconUsers}
              onClick={() => {
                setModalState('credit');
              }}
              visible={isCreditDesignationVisible}
            />
            <Table.RowAction.Option
              label="Edit Pledge Amount"
              icon={IconUsers}
              onClick={() => {
                setIsEditPledgeOpened(true);
              }}
              visible={cartData?.status == 'pledged'}
            />
            <Table.RowAction.Option
              label="Cancel Pledge"
              icon={IconArrowsDoubleNeSwTabler}
              onClick={() => openStack(FullOrderModalStackModals.RefundOrder)}
              // Only Show this if the is pledged, not completed
              visible={cartData.status === 'pledged'}
            />
            <Table.RowAction.Option
              label="View Full Order"
              icon={IconReceipt}
              onClick={() => {
                openStack(FullOrderModalStackModals.FullOrder);
              }}
              visible={isViewFullOrderVisible}
            />
          </>
        )}
      </Table.RowAction>

      <FullOrderModalStack stack={stack} order={cartData} />
      {/*
       TODO: Make the edit credit designation modal a component like the resend/refund ones

      */}
      <EditCreditDesignationModal
        opened={modalState === 'credit'}
        onClose={() => setModalState(undefined)}
        cartId={cartData.uuid}
        cartItemId={props.row.original.uuid}
      />
      <EditPledgeModal
        opened={isEditPledgeOpened}
        onClose={() => setIsEditPledgeOpened(false)}
        cart={cartData}
        cartItemId={props.row.original.uuid}
      />
    </>
  );
};

export { CampaignActions, CampaignDonorNameHeader };
